import { z } from "zod";

/**
 * Contratos da conexao com o CRM (Kommo) de um cliente.
 *
 * O token de acesso so ENTRA (em `conectarKommoSchema`) e nunca sai: nenhuma
 * resposta da API o devolve, nem mascarado.
 */

export const SITUACOES_DA_CONEXAO = ["nao-conectada", "conectada", "sincronizando", "com-erro"] as const;
export const situacaoDaConexaoSchema = z.enum(SITUACOES_DA_CONEXAO);
export type SituacaoDaConexao = z.infer<typeof situacaoDaConexaoSchema>;

export const etapaDoKommoSchema = z.object({
  id: z.number().int(),
  nome: z.string(),
  /** `ganha` (142) e `perdida` (143) sao as etapas fixas de fechamento do Kommo. */
  tipo: z.enum(["aberta", "ganha", "perdida"]),
});
export type EtapaDoKommo = z.infer<typeof etapaDoKommoSchema>;

export const pipelineDoKommoSchema = z.object({
  id: z.number().int(),
  nome: z.string(),
  principal: z.boolean(),
  etapas: z.array(etapaDoKommoSchema),
});
export type PipelineDoKommo = z.infer<typeof pipelineDoKommoSchema>;

/** Uma etapa de um pipeline do Kommo. */
export const etapaSelecionadaSchema = z
  .object({ pipelineId: z.number().int(), etapaId: z.number().int() })
  .strict();
export type EtapaSelecionada = z.infer<typeof etapaSelecionadaSchema>;

/** Itens do funil que vem do CRM, na ordem. O Gerenciador vem da midia. */
export const ITENS_DO_FUNIL_NO_CRM = ["crm", "mql", "agendado", "consulta", "venda"] as const;
export type ItemDoFunilNoCrm = (typeof ITENS_DO_FUNIL_NO_CRM)[number];

/**
 * Qual etapa do Kommo representa cada item do funil. Um lead "chegou" ao item
 * quando entra nessa etapa (historico `lead_status_changed`) ou numa etapa de
 * item posterior. Na Venda, a data e o valor sao os do fechamento do lead.
 */
export const mapeamentoDoFunilSchema = z
  .object({
    crm: etapaSelecionadaSchema,
    mql: etapaSelecionadaSchema,
    agendado: etapaSelecionadaSchema,
    consulta: etapaSelecionadaSchema,
    venda: etapaSelecionadaSchema,
    /** Etapa de tratamento: so conta no faturamento de tratamento quem passou por ela. */
    tratamento: etapaSelecionadaSchema.optional(),
    /** Campo numerico "Valor Consulta". */
    valorConsultaCampoId: z.number().int().nullable().optional(),
    /** Campo numerico "Valor proposto" (propostas em aberto). */
    valorPropostoCampoId: z.number().int().nullable().optional(),
    /** Campo do Kommo que vira Campanha no ranking/listagem (padrao: utm_campaign). */
    utmCampanhaCampoId: z.number().int().nullable().optional(),
    /** Campo do Kommo que vira Conjunto (padrao: utm_content). */
    utmConjuntoCampoId: z.number().int().nullable().optional(),
    /** Campo do Kommo que vira Anuncio (padrao: utm_term). */
    utmAnuncioCampoId: z.number().int().nullable().optional(),
  })
  .strict();
export type MapeamentoDoFunil = z.infer<typeof mapeamentoDoFunilSchema>;

/** Tipos de campo do Kommo que viram filtro: os de escolha e os de rastreio (UTM). */
export const TIPOS_DE_CAMPO_FILTRAVEIS = [
  "select",
  "multiselect",
  "radiobutton",
  "category",
  "tracking_data",
  "text",
] as const;

/** Campos de dinheiro do Kommo: Valor Consulta, Valor proposto. */
export const TIPOS_DE_CAMPO_MONETARIOS = ["numeric", "price", "monetary"] as const;

/** Campo personalizado de lead no Kommo. `opcoes` vazio em campo de texto livre. */
export const campoPersonalizadoSchema = z.object({
  id: z.number().int(),
  nome: z.string(),
  tipo: z.string(),
  opcoes: z.array(z.string()),
});
export type CampoPersonalizado = z.infer<typeof campoPersonalizadoSchema>;

/** Quais campos personalizados aparecem nos filtros do Dashboard. */
export const camposDoFiltroSchema = z
  .object({ campos: z.array(z.number().int()).max(20, "escolha no maximo 20 campos") })
  .strict();
export type CamposDoFiltro = z.infer<typeof camposDoFiltroSchema>;

/** Uma condicao do recorte: o campo do Kommo tem este valor. */
export const condicaoDoRecorteSchema = z
  .object({
    campoId: z.number().int(),
    valor: z.string().trim().min(1).max(200),
  })
  .strict();
export type CondicaoDoRecorte = z.infer<typeof condicaoDoRecorteSchema>;

/** Condicoes de um grupo, ligadas por E ou OU: "campo e tal E/OU campo e tal". */
export const grupoDoRecorteSchema = z
  .object({
    operador: z.enum(["e", "ou"]),
    condicoes: z.array(condicaoDoRecorteSchema).min(1).max(10),
  })
  .strict();
export type GrupoDoRecorte = z.infer<typeof grupoDoRecorteSchema>;

/** Recorte do projeto: grupos ligados por E ou OU. */
export const recorteDoProjetoSchema = z
  .object({
    operador: z.enum(["e", "ou"]),
    grupos: z.array(grupoDoRecorteSchema).min(1).max(10),
  })
  .strict();
export type RecorteDoProjeto = z.infer<typeof recorteDoProjetoSchema>;

export function condicoesDoRecorte(recorte: RecorteDoProjeto): readonly CondicaoDoRecorte[] {
  return recorte.grupos.flatMap((grupo) => grupo.condicoes);
}

export function recorteAtende(
  recorte: RecorteDoProjeto,
  temValor: (campoId: number, valor: string) => boolean,
): boolean {
  const grupoOk = (grupo: GrupoDoRecorte) =>
    grupo.operador === "ou"
      ? grupo.condicoes.some((condicao) => temValor(condicao.campoId, condicao.valor))
      : grupo.condicoes.every((condicao) => temValor(condicao.campoId, condicao.valor));
  return recorte.operador === "ou" ? recorte.grupos.some(grupoOk) : recorte.grupos.every(grupoOk);
}

export const definirRecorteDoProjetoSchema = z
  .object({ recorte: recorteDoProjetoSchema.nullable() })
  .strict();
export type DefinirRecorteDoProjeto = z.infer<typeof definirRecorteDoProjetoSchema>;

export const conexaoDoKommoSchema = z.object({
  situacao: situacaoDaConexaoSchema,
  subdominio: z.string().nullable(),
  nomeDaConta: z.string().nullable(),
  mapeamento: mapeamentoDoFunilSchema.nullable(),
  pipelines: z.array(pipelineDoKommoSchema),
  ultimaSincronizacaoEm: z.iso.datetime().nullable(),
  /** Mensagem da ultima falha, ja sem nenhum dado sensivel. */
  ultimoErro: z.string().nullable(),
  /** Campos de lead que podem virar filtro (so os tipos filtraveis). */
  camposPersonalizados: z.array(campoPersonalizadoSchema),
  /** Ids dos campos em uso nos filtros do Dashboard. */
  camposDoFiltro: z.array(z.number().int()),
  /** Filtro permanente deste projeto (ex.: um doutor num funil compartilhado). */
  recorte: recorteDoProjetoSchema.nullable(),
});
export type ConexaoDoKommo = z.infer<typeof conexaoDoKommoSchema>;

/** Aceita "clinica", "clinica.kommo.com" ou "https://clinica.kommo.com/..." e guarda so o subdominio. */
export const subdominioDoKommoSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((valor) => valor.replace(/^https?:\/\//, "").split(/[./]/)[0] ?? "")
  .pipe(
    z
      .string()
      .min(2, "informe o endereço da conta Kommo")
      .max(63)
      .regex(/^[a-z0-9-]+$/, "use só o nome da conta, como em suaclinica.kommo.com"),
  );

export const conectarKommoSchema = z
  .object({
    subdominio: subdominioDoKommoSchema,
    token: z.string().trim().min(20, "token inválido").max(5000, "token inválido"),
  })
  .strict();
export type ConectarKommo = z.infer<typeof conectarKommoSchema>;

/** Um campo com os valores que existem nos leads sincronizados. */
export const opcaoDeFiltroSchema = z.object({
  campoId: z.number().int(),
  nome: z.string(),
  opcoes: z.array(z.object({ valor: z.string(), quantidade: z.number().int().min(0) })),
});
export type OpcaoDeFiltro = z.infer<typeof opcaoDeFiltroSchema>;

export const opcoesDeFiltroSchema = z.object({ dados: z.array(opcaoDeFiltroSchema) });
export type OpcoesDeFiltro = z.infer<typeof opcoesDeFiltroSchema>;

/** Filtro aplicado: dentro de um campo vale qualquer valor; entre campos, todos. */
export type FiltroDeCampo = { readonly campoId: number; readonly valores: readonly string[] };

/**
 * Lead sem origem preenchida. Nao e um valor do Kommo: o filtro do
 * Gerenciador usa isto para incluir origem vazia.
 */
export const SEM_ORIGEM = "Sem origem";

const textoDoFiltro = (valor: string) =>
  valor
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

/** Meta, Google, organico, ou origem que o Kommo nao conseguiu identificar. */
export function ehValorDoGerenciador(valor: string): boolean {
  if (valor === SEM_ORIGEM) return true;
  const texto = textoDoFiltro(valor);
  if (texto === "") return true;
  return (
    texto.includes("meta") ||
    texto.includes("google") ||
    texto.includes("organic") ||
    texto.includes("nao identificad")
  );
}

/** Opcoes do campo Origem que o atalho Gerenciador marca, mais a origem vazia. */
export function valoresDoGerenciador(opcoes: readonly string[]): string[] {
  return [...new Set([...opcoes.filter((valor) => valor !== SEM_ORIGEM && ehValorDoGerenciador(valor)), SEM_ORIGEM])];
}

/**
 * O filtro da tela e so o atalho Gerenciador. Nesse caso o investimento
 * continua sendo o gasto de Meta e Google: organico nao tem midia.
 */
export function filtroDoGerenciador(filtros: readonly FiltroDeCampo[], campoDeOrigemId: number | null): boolean {
  if (campoDeOrigemId === null || filtros.length !== 1) return false;
  const filtro = filtros[0];
  if (!filtro || filtro.campoId !== campoDeOrigemId || !filtro.valores.includes(SEM_ORIGEM)) return false;
  return filtro.valores.every((valor) => ehValorDoGerenciador(valor));
}

/** O lead entra no filtro. `Sem origem` casa com campo ausente ou em branco. */
export function leadAtendeValores(valoresDoLead: readonly string[] | undefined, aceitos: readonly string[]): boolean {
  const incluiVazio = aceitos.includes(SEM_ORIGEM);
  const reais = new Set(aceitos.filter((valor) => valor !== SEM_ORIGEM));
  const preenchidos = (valoresDoLead ?? []).map((valor) => valor.trim()).filter((valor) => valor !== "");
  if (preenchidos.some((valor) => reais.has(valor))) return true;
  return incluiVazio && preenchidos.length === 0;
}

/**
 * Filtro na URL: `filtro=<campoId>:<valor>`, repetido. O valor pode ter `:`;
 * so o primeiro separa o campo.
 */
export function filtrosParaConsulta(filtros: readonly FiltroDeCampo[]): string[] {
  return filtros.flatMap((filtro) => filtro.valores.map((valor) => `${filtro.campoId}:${valor}`));
}

const itemDeFiltroSchema = z
  .string()
  .regex(/^\d+:.+$/, "use campoId:valor")
  .max(300);

function lerFiltros(filtro: string | string[] | undefined): FiltroDeCampo[] {
  const itens = filtro === undefined ? [] : Array.isArray(filtro) ? filtro : [filtro];
  const porCampo = new Map<number, string[]>();
  for (const item of itens) {
    const separador = item.indexOf(":");
    const campoId = Number(item.slice(0, separador));
    porCampo.set(campoId, [...(porCampo.get(campoId) ?? []), item.slice(separador + 1)]);
  }
  return [...porCampo].map(([campoId, valores]) => ({ campoId, valores }));
}

export const consultaDoDashboardSchema = z
  .object({
    inicio: z.iso.date(),
    fim: z.iso.date(),
    filtro: z.union([itemDeFiltroSchema, z.array(itemDeFiltroSchema).max(100)]).optional(),
  })
  .refine((consulta) => consulta.inicio <= consulta.fim, {
    message: "o inicio precisa ser antes do fim",
    path: ["fim"],
  })
  .transform(({ inicio, fim, filtro }) => ({ periodo: { inicio, fim }, filtros: lerFiltros(filtro) }));
export type ConsultaDoDashboard = z.output<typeof consultaDoDashboardSchema>;

/** Listagem de oportunidades da etapa do funil (RF-13). Mesmo periodo e filtros do resumo. */
export const consultaDeOportunidadesSchema = z
  .object({
    inicio: z.iso.date(),
    fim: z.iso.date(),
    filtro: z.union([itemDeFiltroSchema, z.array(itemDeFiltroSchema).max(100)]).optional(),
    etapa: z.enum(ITENS_DO_FUNIL_NO_CRM).default("crm"),
    cursor: z.string().min(1).optional(),
    limite: z.coerce.number().int().min(1).max(200).default(50),
  })
  .refine((consulta) => consulta.inicio <= consulta.fim, {
    message: "o inicio precisa ser antes do fim",
    path: ["fim"],
  })
  .transform(({ inicio, fim, filtro, etapa, cursor, limite }) => ({
    periodo: { inicio, fim },
    filtros: lerFiltros(filtro),
    etapa,
    cursor,
    limite,
  }));
export type ConsultaDeOportunidades = z.output<typeof consultaDeOportunidadesSchema>;

/** Contatos que tiveram lead no periodo, com o historico completo de oportunidades. */
export const consultaDeHistoricoDeContatosSchema = z
  .object({
    inicio: z.iso.date(),
    fim: z.iso.date(),
    filtro: z.union([itemDeFiltroSchema, z.array(itemDeFiltroSchema).max(100)]).optional(),
    cursor: z.string().min(1).optional(),
    limite: z.coerce.number().int().min(1).max(200).default(50),
  })
  .refine((consulta) => consulta.inicio <= consulta.fim, {
    message: "o inicio precisa ser antes do fim",
    path: ["fim"],
  })
  .transform(({ inicio, fim, filtro, cursor, limite }) => ({
    periodo: { inicio, fim },
    filtros: lerFiltros(filtro),
    cursor,
    limite,
  }));
export type ConsultaDeHistoricoDeContatos = z.output<typeof consultaDeHistoricoDeContatosSchema>;

/** Consulta do realizado mes a mes: o ano e os mesmos filtros do Dashboard. */
export const realizadoMensalConsultaSchema = z
  .object({
    ano: z.coerce.number().int().min(2020).max(2100),
    filtro: z.union([itemDeFiltroSchema, z.array(itemDeFiltroSchema).max(100)]).optional(),
  })
  .transform(({ ano, filtro }) => ({
    ano,
    filtros: consultaDoDashboardSchema.parse({ inicio: "2020-01-01", fim: "2020-01-01", filtro }).filtros,
  }));
export type RealizadoMensalConsulta = z.output<typeof realizadoMensalConsultaSchema>;
